import React, { useState } from 'react';
import {
  Container,
  Text,
  Flex,
  VStack,
  GridItem,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { DeptGrid } from '@rsc/ui-components/dept/design-system';
import Image from '@rsc/ui-components/tailwind/Image';
import Link from '@rsc/ui-components/tailwind/Link';
import { MdArrowForward } from 'react-icons/md';
import { motion } from 'framer-motion';
import SanityBackgroundImage from '../../../atoms/SanityBackgroundImage';

function CategoryCard({ categoryCard }) {
  const theme = useTheme();
  const [isLgScreen] = useMediaQuery(`(min-width: ${theme.breakpoints.lg})`);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <VStack
      key={categoryCard._key}
      as={Link}
      link={categoryCard?.link}
      pos="relative"
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        '&:hover button': {
          opacity: '1',
        },
        img: {
          overflow: 'visible !important',
        },
      }}
    >
      <SanityBackgroundImage
        gradientGenerator={url =>
          `linear-gradient(1deg, rgb(0 0 0 / 70%) 14.58%, rgba(0, 0, 0, 0.00) 100%), url(${url}) #d3d3d324 50% / cover no-repeat`
        }
        as={Flex}
        image={categoryCard.media?.image}
        aspectRatio={[1, null, 9 / 9.5]}
        borderRadius="lg"
        width="full"
        p={6}
        flexDirection="column"
        transition="filter .1s ease-in-out"
        _hover={{ filter: 'brightness(.6)' }}
        justifyContent="space-between"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={isLgScreen && isHovered ? { opacity: 1 } : { opacity: 0 }}
          transition={{ ease: 'easeOut', duration: 0.4 }}
        >
          <IconButton
            aria-label="open"
            maxWidth={8}
            height={10}
            borderRadius={30}
            icon={<MdArrowForward size={24} />}
            left={{
              base: '89%',
              xl: '91%',
            }}
          />
        </motion.div>
        <Box>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            mb={{
              base: 4,
              lg: 8,
            }}
            gap={4}
          >
            <Text
              color="typography.lighter !important"
              as="h2"
              textStyle="h2"
              fontWeight="black"
              fontSize={{
                base: 32,
                md: 28,
                xl: 40,
              }}
            >
              {categoryCard.headline}
            </Text>
            {categoryCard.icon && (
              <Image
                className="max-w-[5.59rem] md:max-w-16 xl:max-w-[5.59rem] [&>img]:object-contain"
                ratio={null}
                sanityImage={categoryCard.icon}
                srcSetBasisWidth={300}
              />
            )}
          </Flex>
          {categoryCard.attributes.map((attribute, index) => (
            <Flex
              key={attribute._key}
              borderBottom={
                index === categoryCard.attributes.length - 1
                  ? '0px'
                  : '1px solid'
              }
              borderColor="rgba(226, 229, 233, 0.20)"
              color="typography.lighter"
              justifyContent="space-between"
              py={2}
              gap={{
                base: 4,
                md: 2,
                xl: 4,
              }}
            >
              <Text
                fontSize={{
                  base: '1rem',
                  md: '0.875rem',
                  xl: '1rem',
                }}
              >
                {attribute.name}
              </Text>
              <Text textAlign="right" fontWeight="bold">
                {attribute.value}
              </Text>
            </Flex>
          ))}
        </Box>
      </SanityBackgroundImage>
    </VStack>
  );
}

function CardRowSubCategory({ block }) {
  if (!block?.categoryCards) return null;

  return (
    <Container pt={[8, null, 10]} pb={[8, null, 20]}>
      <DeptGrid>
        {block.categoryCards.map(categoryCard => (
          <GridItem
            key={categoryCard._key}
            colSpan={{
              base: 6,
              md: 3,
              lg: 4,
            }}
          >
            <CategoryCard categoryCard={categoryCard} />
          </GridItem>
        ))}
      </DeptGrid>
    </Container>
  );
}
CategoryCard.propTypes = {
  _key: PropTypes.string.isRequired,
  headline: PropTypes.string.isRequired,
  media: PropTypes.shape({
    image: PropTypes.shape({
      asset: PropTypes.shape({
        url: PropTypes.string,
      }),
      alt: PropTypes.string,
    }),
  }),
  icon: PropTypes.shape({
    asset: PropTypes.shape({
      url: PropTypes.string,
    }),
  }),
  link: PropTypes.shape({
    linkType: PropTypes.string,
    href: PropTypes.string,
    internalLink: PropTypes.shape({
      current: PropTypes.string,
    }),
  }),
  attributes: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ),
}.isRequired;

CardRowSubCategory.propTypes = {
  block: PropTypes.shape({
    categoryCards: PropTypes.arrayOf(
      PropTypes.shape({
        _key: PropTypes.string.isRequired,
        headline: PropTypes.string.isRequired,
        media: PropTypes.shape({
          image: PropTypes.shape({
            asset: PropTypes.shape({
              url: PropTypes.string,
            }),
            alt: PropTypes.string,
          }),
        }),
        icon: PropTypes.shape({
          asset: PropTypes.shape({
            url: PropTypes.string,
          }),
        }),
        link: PropTypes.shape({
          linkType: PropTypes.string,
          href: PropTypes.string,
          internalLink: PropTypes.shape({
            current: PropTypes.string,
          }),
        }),
        attributes: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
          })
        ),
      })
    ),
  }).isRequired,
};

export default CardRowSubCategory;
